import { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  InputAdornment,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import {
  locationService,
  shopService,
} from '../../services'
import ShopDocumentUploadField from './ShopDocumentUploadField'
import {
  buildBdFullPhone,
  formatBdPhoneInput,
  isValidPhoneLength,
} from '../../utils/phone'
import {
  formatCustomerCodeInput,
  isValidCustomerCode,
} from '../../utils/customerCode'

const SEARCH_DEBOUNCE_MS = 450

function useDebouncedValue(value, ms) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return debounced
}

export default function CreateShopWizard({ open, onClose, onCompleted }) {
  const [step, setStep] = useState(1)
  const [ownerName, setOwnerName] = useState('')
  const [customerCode, setCustomerCode] = useState('')
  const [phone, setPhone] = useState('')
  const [shopName, setShopName] = useState('')
  const [address, setAddress] = useState('')
  const [email, setEmail] = useState('')
  const [geolocation, setGeolocation] = useState('')
  const [createdOrgId, setCreatedOrgId] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  const [locSearch, setLocSearch] = useState('')
  const debouncedLocSearch = useDebouncedValue(locSearch, SEARCH_DEBOUNCE_MS)
  const [locPage, setLocPage] = useState(0)
  const [locRowsPerPage, setLocRowsPerPage] = useState(10)
  const [locTotal, setLocTotal] = useState(0)
  const [locPageItems, setLocPageItems] = useState([])
  const [locLoading, setLocLoading] = useState(false)
  const [selectedLocations, setSelectedLocations] = useState(() => new Map())

  const reset = useCallback(() => {
    setStep(1)
    setOwnerName('')
    setCustomerCode('')
    setPhone('')
    setShopName('')
    setAddress('')
    setEmail('')
    setGeolocation('')
    setCreatedOrgId(null)
    setSubmitting(false)
    setError(null)
    setLocSearch('')
    setLocPage(0)
    setLocTotal(0)
    setLocPageItems([])
    setSelectedLocations(new Map())
  }, [])

  useEffect(() => {
    if (!open) return
    reset()
  }, [open, reset])

  useEffect(() => {
    if (!open || step !== 2) return
    let cancelled = false
    ;(async () => {
      try {
        setLocLoading(true)
        const q = debouncedLocSearch.trim()
        const data = await locationService.getLocations({
          page: locPage + 1,
          limit: locRowsPerPage,
          ...(q ? { search: q } : {}),
        })
        if (cancelled) return
        setLocPageItems(data?.items || [])
        setLocTotal(data?.pagination?.total ?? 0)
      } catch {
        if (!cancelled) {
          setLocPageItems([])
          setLocTotal(0)
        }
      } finally {
        if (!cancelled) setLocLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, step, locPage, locRowsPerPage, debouncedLocSearch])

  const step1Valid =
    ownerName.trim() &&
    isValidCustomerCode(customerCode) &&
    isValidPhoneLength(phone, 10, 10) &&
    shopName.trim() &&
    address.trim()

  const handleCustomerCodeChange = (value) => {
    setCustomerCode(formatCustomerCodeInput(value))
  }

  const handlePhoneChange = (value) => {
    const formatted = formatBdPhoneInput(value)
    if (formatted !== null) {
      setPhone(formatted)
    }
  }

  const handleCreateStep1 = async () => {
    if (!phone || !isValidPhoneLength(phone, 10, 10)) {
      setError('Please enter a valid 10-digit phone number')
      return
    }
    if (!isValidCustomerCode(customerCode)) {
      setError('Shop ID must be exactly 10 digits and start with 9')
      return
    }

    const e164 = buildBdFullPhone(phone)

    setSubmitting(true)
    setError(null)
    try {
      const result = await shopService.createShop({
        customer_code: formatCustomerCodeInput(customerCode),
        shop_owner_name: ownerName.trim(),
        phone: e164,
        shop_name: shopName.trim(),
        address: address.trim(),
        ...(email.trim() ? { email: email.trim() } : {}),
        ...(geolocation.trim() ? { geolocation: geolocation.trim() } : {}),
      })
      const orgId = result.org_id
      setCreatedOrgId(orgId)
      setStep(2)
    } catch (e) {
      setError(
        e.response?.data?.message || e.message || 'Failed to create shop',
      )
    } finally {
      setSubmitting(false)
    }
  }

  const toggleLocation = (loc) => {
    setSelectedLocations((prev) => {
      const next = new Map(prev)
      if (next.has(loc.id)) next.delete(loc.id)
      else next.set(loc.id, loc)
      return next
    })
  }

  const handleFinishStep2 = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const ids = [...selectedLocations.keys()]
      if (ids.length > 0) {
        await shopService.addLocationMappings(createdOrgId, ids)
      }
      onCompleted?.()
      onClose()
    } catch (e) {
      setError(
        e.response?.data?.message || e.message || 'Failed to save location mapping',
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={!submitting ? onClose : undefined} fullWidth maxWidth="md">
      <DialogTitle>
        {step === 1 ? 'Create Shop — Details' : 'Create Shop — Location Mapping'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {step === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Shop Owner Name"
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Shop ID"
              value={customerCode}
              onChange={(e) => handleCustomerCodeChange(e.target.value)}
              required
              fullWidth
              helperText="Exactly 10 digits starting with 9 (e.g. 9123456789)"
              inputProps={{ inputMode: 'numeric', maxLength: 10 }}
            />
            <TextField
              label="Contact Number"
              value={phone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              required
              fullWidth
              helperText="Enter 10 digits (e.g. 1712-345678)"
              inputProps={{ inputMode: 'numeric', maxLength: 11 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">+880</InputAdornment>
                ),
              }}
            />
            <TextField
              label="Shop Name"
              value={shopName}
              onChange={(e) => setShopName(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              required
              fullWidth
              multiline
              minRows={3}
            />
            <TextField
              label="Email (optional)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
            />
            <TextField
              label="Geolocation (optional)"
              value={geolocation}
              onChange={(e) => setGeolocation(e.target.value)}
              fullWidth
              helperText="latitude,longitude"
            />
          </Box>
        )}

        {step === 2 && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Customer Code (Shop ID): <strong>{createdOrgId}</strong>. Default
              warehouse mapping was applied. You can add or replace documents,
              map locations below, or skip to finish.
            </Alert>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              Shop documents (optional)
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
                mb: 3,
              }}
            >
              <ShopDocumentUploadField
                orgId={createdOrgId}
                docType="trade_license"
                label="Trade License"
              />
              <ShopDocumentUploadField
                orgId={createdOrgId}
                docType="nid"
                label="NID"
              />
              <ShopDocumentUploadField
                orgId={createdOrgId}
                docType="shop_photo"
                label="Shop Photo"
              />
            </Box>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Shop mapping (optional)
            </Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="Search locations…"
              value={locSearch}
              onChange={(e) => {
                setLocSearch(e.target.value)
                setLocPage(0)
              }}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            {locLoading ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" />
                        <TableCell>Name</TableCell>
                        <TableCell>Code</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {locPageItems.map((loc) => (
                        <TableRow key={loc.id} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedLocations.has(loc.id)}
                              onChange={() => toggleLocation(loc)}
                            />
                          </TableCell>
                          <TableCell>{loc.name}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>
                            {loc.code}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={locTotal}
                  page={locPage}
                  onPageChange={(_, p) => setLocPage(p)}
                  rowsPerPage={locRowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setLocRowsPerPage(parseInt(e.target.value, 10))
                    setLocPage(0)
                  }}
                  rowsPerPageOptions={[10, 25, 50]}
                />
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {step === 1 ? (
          <>
            <Button onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateStep1}
              disabled={!step1Valid || submitting}
            >
              {submitting ? 'Creating…' : 'Create Shop & Continue'}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleFinishStep2} disabled={submitting}>
              {selectedLocations.size ? 'Save & Finish' : 'Discard & Finish'}
            </Button>
            <Button
              variant="contained"
              onClick={handleFinishStep2}
              disabled={submitting}
            >
              {submitting ? 'Saving…' : 'Save & Finish'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
